import { lazy, Suspense, useCallback, useMemo } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useAuthorization } from '@hooks/useAuthorization';
import { LoadingState } from '@/components/ui';
// KAN-1181: seller pages are code-split so the workspace shell (rail + topbar) loads
// first and each surface (analytics/settlement/inventory/etc.) is fetched only when
// navigated to.
const SellerDashboard = lazy(() => import('../components/seller/SellerDashboard'));
const SellerProducts = lazy(() => import('../components/seller/SellerProducts'));
const SellerOrders = lazy(() => import('../components/seller/SellerOrders'));
const SellerAnalytics = lazy(() => import('../components/seller/SellerAnalytics'));
const SellerSettlement = lazy(() => import('../components/seller/SellerSettlement'));
const SellerProfile = lazy(() => import('../components/seller/SellerProfile'));
const CreateProduct = lazy(() => import('../components/seller/CreateProduct'));
const EditProduct = lazy(() => import('../components/seller/EditProduct'));
const SellerInventory = lazy(() => import('../components/seller/SellerInventory'));
const SellerTeam = lazy(() => import('../components/seller/SellerTeam'));
const SellerStorefrontStudio = lazy(() => import('../components/seller/SellerStorefrontStudio'));
import NotificationsBell from '@/components/retail/NotificationsBell';
import { useToast } from '@/components/feedback/ToastProvider';
import { getCustomerPortalUrl } from '@/config/portalUrls';
import type { NotificationItem } from '@services/notificationsApi';
import { useAuth } from '@context/AuthContext';
import {
  RetailAppShell,
  useRetailNav,
  type RetailNavSection
} from '@portals/shared/components/RetailAppShell';
import OtpVerificationNotice from '@components/security/OtpVerificationNotice';
import './SellerPortal.css';

/**
 * KAN-2035 — the seller workspace navigation, grouped exactly as the approved mock
 * (Sell / Money / Shop). <DhruvantaAppShell> renders it into the left rail and folds it
 * into its own accessible mobile drawer below 880px, so the bespoke <SellerNavbar> and
 * its hand-rolled hamburger are retired.
 */
const NAV_SECTIONS: RetailNavSection[] = [
  {
    id: 'sell',
    label: 'Sell',
    items: [
      { to: '/dashboard', label: 'Dashboard', icon: '📊' },
      { to: '/orders', label: 'Orders', icon: '📋' },
      { to: '/products', label: 'Catalogue', icon: '📦' },
      { to: '/inventory', label: 'Inventory', icon: '📥' }
    ]
  },
  {
    id: 'money',
    label: 'Money',
    items: [
      // Payouts are served by dhruvanta-settlement (the mock's "money surfaces name the
      // platform services" decision).
      { to: '/settlement', label: 'Payouts', icon: '💸' },
      { to: '/analytics', label: 'Analytics', icon: '📈' }
    ]
  },
  {
    id: 'shop',
    label: 'Shop',
    items: [
      { to: '/storefront', label: 'Storefront', icon: '🪄' },
      { to: '/team', label: 'Staff', icon: '🧑‍🤝‍🧑' },
      { to: '/profile', label: 'Store profile', icon: '👤' }
    ]
  }
];

/** Initials for the topbar avatar, e.g. "Ravi Patel" → "RP". */
export const initialsOf = (name?: string | null): string => {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'S';
  return parts
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase() ?? '')
    .join('');
};

function SellerPortal() {
  const { isAuthenticated, hasRole } = useAuthorization();
  const { authState, logout } = useAuth();
  const navigate = useNavigate();
  const { pushToast } = useToast();
  const nav = useRetailNav(NAV_SECTIONS);
  const storePortalUrl = getCustomerPortalUrl('/retail');

  const handleNotificationToast = useCallback(
    (notification: NotificationItem) => {
      pushToast({
        variant: 'info',
        message: notification.title,
        description: notification.body
      });
    },
    [pushToast]
  );

  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/login');
  }, [logout, navigate]);

  const storeName = authState.user?.name;

  const topbarEnd = useMemo(
    () => (
      <>
        {storeName ? <span className="rt-chip">{storeName}</span> : null}
        <NotificationsBell
          autoRefreshMs={60_000}
          theme="dark"
          onNewNotification={handleNotificationToast}
        />
        <a
          href={storePortalUrl}
          className="rt-btn rt-btn--ghost rt-btn--sm"
          target="_blank"
          rel="noreferrer"
        >
          View store
        </a>
        <button type="button" onClick={handleLogout} className="rt-btn rt-btn--ghost rt-btn--sm">
          Logout
        </button>
        <span className="rt-avatar" aria-hidden="true">
          {initialsOf(storeName)}
        </span>
      </>
    ),
    [handleLogout, handleNotificationToast, storeName, storePortalUrl]
  );

  // Redirect non-sellers
  if (!isAuthenticated || !hasRole(['SELLER', 'SUPER_ADMIN'])) {
    return <Navigate to="/register" replace />;
  }

  return (
    <RetailAppShell
      role="Seller"
      nav={nav}
      navAriaLabel="Seller workspace"
      searchPlaceholder="Search orders, SKUs…"
      topbarEnd={topbarEnd}
    >
      <div className="seller-content rt-content">
        <OtpVerificationNotice portal="seller" profilePath="/profile" className="seller-otp-notice" />
        <Suspense fallback={<LoadingState label="Loading…" />}>
        <Routes>
          <Route path="/" element={<SellerDashboard />} />
          <Route path="/dashboard" element={<SellerDashboard />} />
          <Route path="/products" element={<SellerProducts />} />
          <Route path="/products/create" element={<CreateProduct />} />
          <Route path="/products/:productId/edit" element={<EditProduct />} />
          <Route path="/inventory" element={<SellerInventory />} />
          <Route path="/orders" element={<SellerOrders />} />
          <Route path="/analytics" element={<SellerAnalytics />} />
          <Route path="/settlement" element={<SellerSettlement />} />
          <Route path="/storefront" element={<SellerStorefrontStudio />} />
          <Route path="/team" element={<SellerTeam />} />
          <Route path="/profile" element={<SellerProfile />} />
        </Routes>
        </Suspense>
      </div>
    </RetailAppShell>
  );
}

export default SellerPortal;
